/**
 * CSV export for the current list view (Operator Lists v2, Task 6).
 *
 * `exportRowsToCsv` is a pure function — no DOM, no formatting decisions of
 * its own; the caller supplies `cellFor` so cell display stays identical to
 * the table (via `formatListCellValue`). `downloadCsv` is the DOM-side half:
 * Blob + object URL + a transient anchor click, guarded for environments
 * (jsdom, SSR) where `URL.createObjectURL` doesn't exist.
 */
import { formatListCellValue, getRowField, type ListCellAttrHint } from '@/shared/formatListCellValue'
import type { ListParams } from '@/app/services/runtimeApi'

export interface ExportColumnSpec {
  name: string
  title: string
}

/** RFC-4180 field quoting: quote when the field contains a comma, quote, CR, or LF. */
function csvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Builds a CSV string from `rows` + `columns`. First line is the column
 * titles; each subsequent line is `cellFor(col, row)` per column. Pure —
 * takes no dependency on formatting internals so callers can plug in
 * `formatListCellValue` (or any other formatter) for parity with the table.
 */
export function exportRowsToCsv<TRow extends Record<string, unknown>>(
  rows: TRow[],
  columns: ExportColumnSpec[],
  cellFor: (col: ExportColumnSpec, row: TRow) => string,
): string {
  const lines: string[] = []
  lines.push(columns.map((col) => csvField(col.title)).join(','))
  for (const row of rows) {
    lines.push(columns.map((col) => csvField(cellFor(col, row))).join(','))
  }
  return lines.join('\r\n')
}

/**
 * Triggers a browser download of `csv` as `filename`. No-op (rather than
 * throwing) when `URL.createObjectURL` isn't available — e.g. under jsdom
 * in tests that don't stub it, or a non-browser environment.
 */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** True for numeric attribute types or currency/percent-formatted columns. */
function isNumericExport(type: string, format?: string): boolean {
  const t = type.toLowerCase()
  return /number|int|float|decimal|numeric/.test(t) || format === 'currency' || format === 'percent'
}

/**
 * One CSV cell for the export. Numeric attrs / currency|percent columns emit the
 * RAW value (`String(value)`) so a re-import parses them as numbers; everything
 * else uses `formatListCellValue` for parity with the on-screen cell. The caller
 * passes `hintFor(col) ?? hintFromColumn(col)` so virtual columns never blank.
 */
export function exportCellValue(
  col: { name: string; format?: string },
  row: Record<string, unknown>,
  hint: ListCellAttrHint,
): string {
  if (isNumericExport(hint.attr_type ?? '', col.format)) {
    const raw = getRowField(row, col.name)
    return raw == null || raw === '' ? '' : String(raw)
  }
  return formatListCellValue(hint, row)
}

export interface ExportParamsInput {
  filters: Record<string, unknown>
  search: string
  searchFields?: string[]
  showDeleted: boolean
  sortField: string
  sortDir: 'asc' | 'desc'
  limit: number
  /** Active i18next locale — resolves translatable reference display server-side. */
  locale?: string
  /** Present only when the calendar view is active — scopes export to the visible month. */
  calendar?: { field: string; from: string; to: string }
}

/** Builds the `runtimeApi.list` params for an export: filtered/searched set, on-screen sort, capped, plus the calendar month range when applicable. */
export function buildExportListParams(i: ExportParamsInput): ListParams {
  const params: ListParams = {
    filters: i.filters,
    search: i.search || undefined,
    search_fields: i.searchFields,
    include_deleted: i.showDeleted || undefined,
    sort_field: i.sortField || undefined,
    sort_dir: i.sortField ? i.sortDir : undefined,
    limit: i.limit,
    locale: i.locale,
  }
  if (i.calendar) {
    params.filter_date_field = i.calendar.field
    params.date_from = i.calendar.from
    params.date_to = i.calendar.to
  }
  return params
}
